import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBoat, setActiveBoat] = useState(null);

  const loadActiveBoat = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/boats/active`, { withCredentials: true, headers: getAuthHeader() });
      setActiveBoat(res.data || null);
    } catch {
      setActiveBoat(null);
    }
  }, []);

  // If user has both boats and households, auto-activate one matching their preferred edition.
  const applyPreferredEdition = useCallback(async () => {
    const pref = localStorage.getItem("preferred_edition");
    if (!pref) return;
    try {
      const activeRes = await axios.get(`${API}/boats/active`, { withCredentials: true, headers: getAuthHeader() });
      const currentKind = activeRes.data?.kind === "household" ? "household" : "boat";
      if (currentKind === pref) return;
      const list = await axios.get(`${API}/boats`, { withCredentials: true, headers: getAuthHeader() });
      const match = (list.data || []).find((b) =>
        pref === "household" ? b.kind === "household" : b.kind !== "household"
      );
      if (match && match.id && match.id !== activeRes.data?.id) {
        await axios.put(`${API}/boats/${match.id}/activate`, {}, { withCredentials: true, headers: getAuthHeader() });
      }
    } catch { /* silent */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true, headers: getAuthHeader() });
      setUser(res.data);
      loadActiveBoat();
    } catch (err) {
      // Offline (ingen response) → behald siste kjente brukar frå localStorage
      // slik at appen kan brukast offline utan å bli tvangsutlogga.
      if (!err?.response && typeof navigator !== "undefined" && !navigator.onLine) {
        try {
          const cached = localStorage.getItem("cached_user");
          if (cached) {
            setUser(JSON.parse(cached));
            loadActiveBoat();
            return;
          }
        } catch { /* ignore parse */ }
      }
      setUser(null);
      setActiveBoat(null);
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
    }
  }, [loadActiveBoat]);

  // Persist last-known user for offline boot
  useEffect(() => {
    if (user) {
      try { localStorage.setItem("cached_user", JSON.stringify(user)); } catch { /* quota */ }
    }
  }, [user]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (res.data.access_token) localStorage.setItem("access_token", res.data.access_token);
    setUser(res.data);
    await applyPreferredEdition();
    loadActiveBoat();
    return res.data;
  };

  const register = async (email, password, name, username, preferredEdition = "boat") => {
    const res = await axios.post(`${API}/auth/register`, {
      email, password, name, username, preferred_edition: preferredEdition,
    }, { withCredentials: true });
    if (res.data.access_token) localStorage.setItem("access_token", res.data.access_token);
    // Remember chosen edition locally for the post-register redirect
    localStorage.setItem("preferred_edition", preferredEdition);
    setUser(res.data);
    await applyPreferredEdition();
    loadActiveBoat();
    return res.data;
  };

  const googleSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google-session`, { session_id: sessionId }, { withCredentials: true });
    if (res.data.access_token) localStorage.setItem("access_token", res.data.access_token);
    setUser(res.data);
    await applyPreferredEdition();
    loadActiveBoat();
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: getAuthHeader() }); } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("cached_user");
    setUser(null);
    setActiveBoat(null);
  };

  const ctxValue = useMemo(
    () => ({ user, loading, login, register, googleSession, logout, checkAuth, activeBoat, loadActiveBoat }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, activeBoat],
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function getAuthHeader() {
  const token = localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useAuth() {
  return useContext(AuthContext);
}

export function usePageAccess(page) {
  const { activeBoat } = useContext(AuthContext);
  const level = activeBoat?.my_access?.[page] || "write";
  return { level, canRead: level !== "none", canWrite: level === "write" };
}
